/**
 * Worklist Domain Types
 *
 * Study assignment and workflow management
 */

import type { WorklistStatus, PriorityLevel } from '@/config/constants'

/**
 * Worklist item domain model
 */
export interface WorklistItem {
  id: string
  studyId: string
  assignedTo: string | null
  assignedBy: string | null
  assignedAt: string | null
  priority: PriorityLevel
  priorityId: string | null // FK to priorities table for dynamic colors
  status: WorklistStatus
  startedAt: string | null
  completedAt: string | null
  dueAt: string | null
  notes: string | null
  // Return workflow fields
  returnedAt: string | null
  returnedBy: string | null
  returnReason: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Create worklist item data
 */
export interface CreateWorklistItemData {
  studyId: string
  assignedTo?: string
  priority?: PriorityLevel
  priorityId?: string // UUID reference to priorities table
  dueAt?: string
  notes?: string
}

/**
 * Update worklist item data
 */
export interface UpdateWorklistItemData {
  assignedTo?: string
  priority?: PriorityLevel
  priorityId?: string // UUID reference to priorities table
  status?: WorklistStatus
  dueAt?: string
  notes?: string
}

/**
 * Worklist search filters
 */
export interface WorklistFilters {
  assignedTo?: string
  status?: WorklistStatus
  priority?: PriorityLevel
  studyId?: string
  dueBefore?: string
  dueAfter?: string
  limit?: number
  offset?: number
}

/**
 * Worklist item with patient and study info (for list views)
 */
export interface WorklistItemWithPatient extends WorklistItem {
  // Patient info
  patientName: string
  patientDocumentType: string
  patientDocumentNumber: string
  // Study info
  modality: string
  studyDescription: string | null
  studyDate: string
  // Assigned user info (optional - only included when joined with users table)
  assignedToName?: string | null
  assignedToEmail?: string | null
}

/**
 * Worklist search result
 */
export interface WorklistSearchResult {
  items: WorklistItemWithPatient[]
  total: number
  limit: number
  offset: number
}

/**
 * Valid status transitions
 */
export const VALID_WORKLIST_STATUS_TRANSITIONS: Record<WorklistStatus, WorklistStatus[]> = {
  PENDING: ['IN_PROGRESS', 'CANCELLED', 'RETURNED'],
  IN_PROGRESS: ['COMPLETED', 'CANCELLED', 'PENDING', 'RETURNED'],
  COMPLETED: ['IN_PROGRESS'],
  CANCELLED: ['PENDING'],
  RETURNED: ['PENDING'],
}

/**
 * Check if status transition is valid
 */
export const isValidWorklistStatusTransition = (
  from: WorklistStatus,
  to: WorklistStatus
): boolean => {
  return VALID_WORKLIST_STATUS_TRANSITIONS[from].includes(to)
}

/**
 * Check if item can be started
 */
export const canStartWorklistItem = (status: WorklistStatus): boolean => {
  return status === 'PENDING'
}

/**
 * Check if item can be completed
 */
export const canCompleteWorklistItem = (status: WorklistStatus): boolean => {
  return status === 'IN_PROGRESS'
}

/**
 * Check if item is overdue
 */
export const isWorklistItemOverdue = (item: WorklistItem): boolean => {
  if (!item.dueAt || item.status === 'COMPLETED' || item.status === 'CANCELLED' || item.status === 'RETURNED') {
    return false
  }
  return new Date(item.dueAt) < new Date()
}

/**
 * Check if item can be returned
 */
export const canReturnWorklistItem = (status: WorklistStatus): boolean => {
  return status === 'PENDING' || status === 'IN_PROGRESS'
}
